import {
	DefaultIsConclusionDialogVal,
	DialogDefaultInteractionStyle,
	PathDefaultInteractionStyle,
	StartDialogDefaultInteractionStyle
} from "@/util/constants";
import { InteractionStyles } from "@/util/enum";
import { has, includes, omit, pull, uniq } from "lodash";
import { v4 } from "uuid";
import {
	Connector,
	Conversation,
	ConversationNode,
	ConversationNodeType,
	Dialog,
	EntityDataSet,
	EntityDataSetDirectory,
	getEntitySetDefaultValue,
	IMetaData,
	INodeCount,
	INodes,
	Path,
	UtteranceValidationResult
} from "./conversation-builder.model";
import { CommonEntity, Entity, EntityValue } from "./models/entity.model";
import { CommonIntent, Intent, Utterance } from "./models/intent.model";

export function generateDefaultConversation(): Conversation {
	return {
		name: "Untitled Conversation",
		description: "",
		imageUrl: null,
		pageId: null,
		pageMetaData: null,
		contentId: null
	};
}

export function selectElementContents(el: HTMLElement) {
	const range = document.createRange();
	range.selectNodeContents(el);
	const sel = window.getSelection();
	sel?.removeAllRanges();
	sel?.addRange(range);
}

export const EntityLengths = {
	app: {
		name: {
			max: 500
		},
		description: {
			max: 1000
		}
	},
	conversation: {
		name: {
			min: 1,
			max: 200
		},
		description: {
			max: 500
		},
		child: {
			dialog: 50,
			path: 499,
			intent: 499,
			entities: 50
		}
	},
	dialog: {
		name: {
			min: 1,
			max: 200
		},
		description: {
			max: 500
		},
		retries: {
			min: 0,
			max: 5
		}
	},
	path: {
		name: {
			min: 1,
			max: 200
		}
	},
	entity: {
		name: {
			min: 1,
			max: 50
		},
		values: {
			maxLength: 250,
			minLength: 1
		}
	},
	intent: {
		name: {
			min: 1,
			max: 50
		},
		utterance: {
			max: 30,
			maxLength: 500,
			minLength: 1
		}
	}
};

export const BuilderAllowedImageTypes = [".jpg", ".jpeg", ".png"];
export const BuilderAllowedImagesMime = ["image/jpeg", "image/png"];

export function countDialogAndPaths(nodes: INodes): INodeCount {
	let pathCount = 0;
	let dialogCount = 0;
	Object.entries(nodes).forEach((element) => {
		const el = element[1] as ConversationNode<Conversation | Dialog | Path | Connector>;
		if (el.type === ConversationNodeType.Conversation || el.type === ConversationNodeType.Dialog) {
			dialogCount += 1;
		} else if (el.type === ConversationNodeType.Path) {
			pathCount += 1;
		}
	});
	return { dialogCount, pathCount };
}

export function countObjects(nodes: INodes, entities: EntityDataSet<Entity>, intents: EntityDataSet<Intent>) {
	const { dialogCount, pathCount } = countDialogAndPaths(nodes);
	const entityCount = entities.ids.length;
	const intentCount = intents.ids.length;

	return { dialogCount, pathCount, entityCount, intentCount };
}

export const assignNewIds = (nodes: INodes, ids: string[]) => {
	const mapIds: { [key: string]: string } = {};
	let str = JSON.stringify(nodes);
	ids.forEach((id) => {
		const newId = `_${v4()}`;
		str = str.replace(new RegExp(id, "g"), newId);
		// save map oldId to newId to use later
		mapIds[newId] = id;
	});
	return { nodes: JSON.parse(str), mapIds };
};

export const mergeCommonIntentIntoAppIntents = (
	commonIntents: CommonIntent[],
	commonEntites: CommonEntity[],
	appIntents: EntityDataSet<Intent>,
	appEntites: EntityDataSet<Entity>
): void => {
	const commonIntentDirectory = getEntitySetDefaultValue(commonIntents).directory;
	const commonEntityDirectory = getEntitySetDefaultValue(commonEntites).directory;
	const appCommonIntents = appIntents.ids
		.filter((intentId) => !!appIntents.directory[intentId].commonIntentId)
		.map((intentId) => appIntents.directory[intentId]);

	const commonEntitiesToRemove: string[] = [];
	const commonEntitiesToAdd: string[] = [];

	appCommonIntents.forEach((appCommonIntent) => {
		const commonIntent = commonIntentDirectory[appCommonIntent.id];

		// Case #1: The common intent is deleted.
		if (!commonIntent) {
			appCommonIntent.commonIntentId = undefined;
			return false;
		}

		// Case #2: If the entity in common intent and metadata(appCommonIntent) has changed.
		if (commonIntent.entityId !== appCommonIntent.entityId) {
			// Case #2.1: If common intent got another entity linked, we need to add that entity to metadata.
			if (commonIntent.entityId) {
				commonEntitiesToAdd.push(commonIntent.entityId);
			}

			// Case #2.2: if appCommonIntent had an entity before, so that needs to be removed if no other reference exists.
			if (appCommonIntent.entityId) {
				commonEntitiesToRemove.push(appCommonIntent.entityId);
			}
		}

		// Step #3.1: Merge of common intent utterances with app common intent utterances.
		const commonIntentUtterances = commonIntent.utterances.map<Utterance>((utterance) => {
			const appIntentUtterance = appCommonIntent.utterances.find((u) => u.id === utterance.id);
			if (appIntentUtterance) {
				return { ...utterance, order: appIntentUtterance.order, key: appIntentUtterance.key };
			}
			return { ...utterance, order: 0, key: 0 };
		});

		// Step #3.2: Merge of common intent with app intent.
		appIntents.directory[appCommonIntent.id] = {
			...commonIntent,
			utterances: commonIntentUtterances,
			key: appCommonIntent.key,
			order: appCommonIntent.order,
			commonIntentId: commonIntent.id
		};
	});

	// Adding new entities to metadata
	commonEntitiesToAdd.forEach((entityId) => {
		const isAlreadyExists = includes(appEntites.ids, entityId);
		if (!isAlreadyExists) {
			appEntites.ids.push(entityId);
		}
		appEntites.directory[entityId] = {
			...commonEntityDirectory[entityId],
			commonEntityId: entityId,
			key: 0,
			order: 0
		};
	});

	// Removing entities from metedata if they have no references
	commonEntitiesToRemove.forEach((entityId) => {
		const isReferenceExists = appIntents.ids.find((id) => appIntents.directory[id].entityId === entityId);
		if (!isReferenceExists) {
			appEntites.ids = pull(appEntites.ids, entityId);
			appEntites.directory = omit(appEntites.directory, [entityId]);
		}
	});
};

export const mergeCommonEntitiesIntoAppEntities = (
	commonEntites: CommonEntity[],
	appEntites: EntityDataSet<Entity>,
	appIntents: EntityDataSet<Intent>
) => {
	const commonEntityDirectory = getEntitySetDefaultValue(commonEntites);
	appEntites.ids.forEach((entityId) => {
		const appEntity = appEntites.directory[entityId];
		const commonEntity = commonEntityDirectory.directory[entityId];

		// Step #1: If the common entity is deleted.
		if (!commonEntity) {
			appEntity.commonEntityId = undefined;
			return false;
		}

		// Step #2: If entity name has been changed. So need to update the entity name in utterances.
		if (commonEntity.name !== appEntity.name) {
			appIntents.ids.forEach((intentId) => {
				const intent = appIntents.directory[intentId];
				const isCommonIntent = intent.commonIntentId;
				if (!isCommonIntent && intent.entityId === entityId) {
					intent.utterances = intent.utterances.map((utterance) => {
						const value = utterance.value.replace(
							new RegExp(`{${appEntity.name}}`, "g"),
							`{${commonEntity.name}}`
						);
						return { ...utterance, value };
					});
				}
			});
		}

		// Step #3: Merge of common entity values with app common entity values.
		const commonEntityValues = commonEntity.values.map<EntityValue>((value) => {
			const appEntityValue = appEntity.values.find((u) => u.id === value.id);
			if (appEntityValue) {
				return { ...value, order: appEntityValue.order, key: appEntityValue.key };
			}
			return { ...value, order: 0, key: 0 };
		});

		// Step #4: Merge of common entity with app entity.
		appEntites.directory[entityId] = {
			...commonEntity,
			values: commonEntityValues,
			key: appEntity.key,
			order: appEntity.order,
			commonEntityId: commonEntity.id
		};
	});
};

/**
 * The function is to remove the entityValueIds from path if they do not exists in entity data.
 * @param nodes
 * @param appIntents
 * @param appEntites
 */
export const invalidateEntityValueIdsInPath = (
	nodes: INodes,
	appIntents: EntityDataSet<Intent>,
	appEntites: EntityDataSet<Entity>
) => {
	for (const nodeId in nodes) {
		const node = nodes[nodeId];
		if (node.type === ConversationNodeType.Path) {
			const data = node.data as Path;

			if (!data.entityValueIds || !data.entityValueIds.length) {
				continue;
			}

			const possibleEntityValueIds = {};
			data.intents.forEach((intentRef) => {
				const intent = appIntents.directory[intentRef.id];
				if (intent && intent.entityId) {
					appEntites.directory[intent.entityId]?.values.forEach((entityValue) => {
						possibleEntityValueIds[entityValue.id] = true;
					});
				}
			});

			data.entityValueIds = data.entityValueIds.filter((valueId) => has(possibleEntityValueIds, valueId));
		}
	}
};

export const UTTERANCE_PATTERN_EXPRESSION = "{(.*?)}";
export const UTTERANCE_INVALIDCHARS_PATTERN = /[\\\(\)\|\^]/g;

export const updateMetaData = (metaData: IMetaData): IMetaData => {
	for (const nodeId in metaData.nodes) {
		const node = metaData.nodes[nodeId];
		if (node.type === ConversationNodeType.Conversation) {
			const data = node.data as Dialog;
			if (typeof data.isHead === "boolean" && data.isHead) {
				if (!data.interactionStyleId) {
					data.interactionStyleId = StartDialogDefaultInteractionStyle;
				}

				if (typeof data.isConclusion !== "boolean") {
					data.isConclusion = DefaultIsConclusionDialogVal; // done as this will be part of conversation dialog table
				}
			}
		} else if (node.type === ConversationNodeType.Dialog) {
			const data = node.data as Dialog;
			if (!data.interactionStyleId) {
				data.interactionStyleId = DialogDefaultInteractionStyle;
			}

			if (typeof data.isConclusion !== "boolean") {
				data.isConclusion = DefaultIsConclusionDialogVal;
			}
		} else if (node.type === ConversationNodeType.Path) {
			const data = node.data as Path;
			if (!data.interactionStyleId) {
				data.interactionStyleId = PathDefaultInteractionStyle;
			}
		}
	}
	return metaData;
};

export const validateUtterances = (
	intent: Intent,
	intentDirectory: EntityDataSetDirectory<Intent>,
	entityDirectory: EntityDataSetDirectory<Entity>
): UtteranceValidationResult => {
	const sameIntentUtterance = intent.utterances.map((u) => u.value.trim().toLowerCase());
	let isUnique = sameIntentUtterance.length === uniq(sameIntentUtterance).length;

	const otherUtterances = Object.keys(intentDirectory)
		.filter((id) => id !== intent.id && intentDirectory[id])
		.flatMap((intentId) => intentDirectory[intentId].utterances.map((u) => u.value.trim().toLowerCase()));

	isUnique = !sameIntentUtterance.some((u) => otherUtterances.some((o) => o === u));

	const usedEntity: Entity = intent.entityId
		? entityDirectory[intent.entityId]
		: { id: "", key: 0, values: [], order: 0, name: "" };
	let isValidEntity = true;
	let invalidEntityName = "";

	if (usedEntity && usedEntity.name) {
		const regex = new RegExp(UTTERANCE_PATTERN_EXPRESSION, "g");
		const entityName = `{${usedEntity.name}}`;

		isValidEntity = !intent.utterances.some((val) => {
			const matches = val.value.match(regex);

			if (matches && matches.length && matches.some((m) => m !== entityName)) {
				return true;
			}

			return false;
		});

		if (!isValidEntity) {
			invalidEntityName = usedEntity.name;
		}
	}

	return { isUnique, isValidEntity, invalidEntityName };
};

export const NormalizeSpaces = (text: string) => {
	if (!text) {
		return text;
	}

	return text.replace(/\s\s+/g, " ");
};

export const isUtterenceValueValid = (text: string) => {
	let isValid: boolean = true;
	const regex = /{([^}]+)}/;
	const entityMatch = text.match(regex);
	let utteranceTextWithRemovedEntityName = text;
	if (entityMatch) {
		// replacing with the second item in array since it has the text between the curly brace which we want to replace with empty string.
		utteranceTextWithRemovedEntityName = text.replace(entityMatch[1], "");
	}
	if (utteranceTextWithRemovedEntityName.match(UTTERANCE_INVALIDCHARS_PATTERN)) {
		isValid = false;
	}

	return isValid;
};
